import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCompany } from '../CompanyContext';
import { salesAPI } from '../../services/api';
import { Sale, SaleStatus, PaymentMethod } from '../../types';
import { useTranslation } from 'react-i18next';
import {
  CurrencyDollarIcon,
  PlusIcon,
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const SalesList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { selectedCompany } = useCompany();
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    if (selectedCompany) {
      fetchSales();
    }
  }, [selectedCompany, statusFilter]);

  const fetchSales = async () => {
    if (!selectedCompany) return;
    
    try {
      setLoading(true);
      setError(null);
      let data: Sale[];
      
      if (statusFilter === 'all') {
        data = await salesAPI.getByCompany(selectedCompany.id);
      } else {
        data = await salesAPI.getByCompany(selectedCompany.id);
        data = data.filter(sale => sale.status === statusFilter);
      }
      
      setSales(data);
    } catch (err) {
      setError(t('sales.errorTitle'));
      console.error('Error fetching sales:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: SaleStatus) => {
    switch (status) {
      case SaleStatus.PENDING:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case SaleStatus.COMPLETED:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case SaleStatus.CANCELLED:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case SaleStatus.REFUNDED:
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: SaleStatus) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case SaleStatus.PENDING:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case SaleStatus.COMPLETED:
        return `${baseClasses} bg-green-100 text-green-800`;
      case SaleStatus.CANCELLED:
        return `${baseClasses} bg-red-100 text-red-800`;
      case SaleStatus.REFUNDED:
        return `${baseClasses} bg-orange-100 text-orange-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getPaymentMethodIcon = (method: PaymentMethod) => {
    switch (method) {
      case PaymentMethod.CASH:
        return <CurrencyDollarIcon className="h-4 w-4 text-green-600" />;
      case PaymentMethod.CREDIT_CARD:
      case PaymentMethod.DEBIT_CARD:
        return <CurrencyDollarIcon className="h-4 w-4 text-blue-600" />;
      case PaymentMethod.BANK_TRANSFER:
        return <CurrencyDollarIcon className="h-4 w-4 text-purple-600" />;
      case PaymentMethod.DIGITAL_WALLET:
        return <CurrencyDollarIcon className="h-4 w-4 text-orange-600" />;
      default:
        return <CurrencyDollarIcon className="h-4 w-4 text-gray-600" />;
    }
  };

  // Refund action removed per requirements

  const formatCurrency = (amount: number) => {
    const currency = selectedCompany?.currency?.code || 'USD';
    console.log('Currency:', currency);
    console.log('Amount:', selectedCompany);
    return new Intl.NumberFormat(i18n.language || 'en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString(i18n.language || 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPaymentMethod = (method: PaymentMethod) => {
    return method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getSaleType = (sale: Sale) => {
    if (sale.orderId) return 'order';
    if (sale.subscriptionId) return 'subscription';
    if (sale.loanId) return 'loan';
    return 'standalone';
  };

  const getSaleTypeDisplay = (sale: Sale) => {
    const type = getSaleType(sale);
    switch (type) {
      case 'order':
        return {
          label: t('sales.types.order'),
          icon: '📦',
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          linkTo: `/orders/${sale.orderId}`,
          reference: `#${sale.orderId?.slice(0, 8)}`
        };
      case 'subscription':
        return {
          label: t('sales.types.subscription'),
          icon: '🔄',
          color: 'text-purple-600',
          bgColor: 'bg-purple-100',
          linkTo: `/subscriptions/${sale.subscriptionId}`,
          reference: t('sales.types.subscriptionPayment')
        };
      case 'loan':
        return {
          label: t('sales.types.loan'),
          icon: '💰',
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          linkTo: `/loans/${sale.loanId}`,
          reference: t('sales.types.loanPayment')
        };
      default:
        return {
          label: t('sales.types.standalone'),
          icon: '💳',
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          linkTo: null,
          reference: t('sales.types.directSale')
        };
    }
  };

  if (!selectedCompany) {
    return (
      <div className="text-center py-12">
        <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('sales.noCompanyTitle')}</h3>
        <p className="mt-1 text-sm text-gray-500">{t('sales.noCompanyText')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <XCircleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('sales.errorTitle')}</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
        <button
          onClick={fetchSales}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
        >
          {t('sales.tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('sales.title')}</h1>
          <p className="text-gray-600">{t('sales.manageFor', { company: selectedCompany.name })}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">{t('sales.filterLabel')}</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">{t('sales.filterAll')}</option>
            <option value={SaleStatus.PENDING}>{t('sales.statuses.PENDING')}</option>
            <option value={SaleStatus.COMPLETED}>{t('sales.statuses.COMPLETED')}</option>
            <option value={SaleStatus.CANCELLED}>{t('sales.statuses.CANCELLED')}</option>
            <option value={SaleStatus.REFUNDED}>{t('sales.statuses.REFUNDED')}</option>
          </select>
        </div>
      </div>

      {/* Sales List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {sales.length === 0 ? (
          <div className="text-center py-12">
            <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('sales.emptyTitle')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {statusFilter === 'all' 
                ? t('sales.emptyAll') 
                : t('sales.emptyFiltered', { status: t(`sales.statuses.${statusFilter}`) })
              }
            </p>
            <div className="mt-6">
              <p className="text-sm text-gray-600">
                {t('sales.note')}
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('sales.table.invoice')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('sales.table.type')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('sales.table.customer')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('sales.table.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('sales.table.total')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('sales.table.paymentMethod')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('sales.table.date')}
                  </th>

                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sales.map((sale) => (
                  <tr 
                    key={sale.id} 
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => window.location.href = `/sales/${sale.id}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(sale.status)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {sale.invoiceNumber}
                          </div>
                          <div className="text-sm text-gray-500">
                            #{sale.id.slice(0, 8)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const typeInfo = getSaleTypeDisplay(sale);
                        return (
                          <div className="flex items-center">
                            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeInfo.bgColor} ${typeInfo.color}`}>
                              <span className="mr-1">{typeInfo.icon}</span>
                              {typeInfo.label}
                            </div>
                          </div>
                        );
                      })()}
                      <div className="text-sm text-gray-500 mt-1">
                        {(() => {
                          const typeInfo = getSaleTypeDisplay(sale);
                          return typeInfo.linkTo ? (
                            <Link
                              to={typeInfo.linkTo}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              {typeInfo.reference}
                            </Link>
                          ) : (
                            <span className="text-gray-500">{typeInfo.reference}</span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{sale.customerName || 'Walk-in Customer'}</div>
                      <div className="text-sm text-gray-500">{sale.customerEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(sale.status)}>
                        {sale.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(sale.total)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getPaymentMethodIcon(sale.paymentMethod)}
                        <span className="ml-2 text-sm text-gray-900">
                          {formatPaymentMethod(sale.paymentMethod)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(sale.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesList;
